import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useAnimation, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, ArrowRight, Zap, Menu, X } from 'lucide-react';
import services from '../data/services';
import hubs from '../data/hubs';

// --- Reusable Reveal Component ---
const RevealSection = ({ children, className = "" }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{ 
        hidden: { opacity: 0, y: 50 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } }
      }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const LocationServicesPage = ({ defaultService }) => {
  const { service, city } = useParams();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [cursorVariant, setCursorVariant] = useState("default");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  const serviceKey = (service || defaultService)?.toLowerCase();
  const cityKey = city?.toLowerCase().replace(/-/g, ' ');
  const serviceData = services[serviceKey];
  const cityExists = hubs.some((hub) => hub.toLowerCase() === cityKey);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    const mouseMove = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('mousemove', mouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', mouseMove);
    };
  }, []);

  if (!serviceData || !cityExists) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-[#f7f6ed] flex flex-col justify-center items-center px-6">
        <h1 className="text-7xl font-black uppercase tracking-tighter">404</h1>
        <Link to="/" className="mt-12 px-8 py-4 border border-white/10 uppercase text-[10px] tracking-widest">Return Home</Link>
      </div>
    );
  }

  const cityLabel = cityKey.split(' ').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');

  const cursorVariants = {
    default: { x: mousePos.x - 16, y: mousePos.y - 16, backgroundColor: "transparent", border: "1px solid #00f3ff" },
    pointer: { x: mousePos.x - 40, y: mousePos.y - 40, height: 80, width: 80, backgroundColor: "#00f3ff", mixBlendMode: "difference", border: "none" }
  };

  return (
    <div className="bg-[#0a0a0a] text-[#f5f5f5] min-h-screen font-sans selection:bg-[#00f3ff] selection:text-black overflow-x-hidden cursor-none">
      <Helmet>
        <title>{serviceData.name} in {cityLabel} | O'Brian Baxter</title>
        <meta name="description" content={`${serviceData.description} Localized for ${cityLabel} clients.`} />
      </Helmet>

      {/* Custom Cursor */}
      <motion.div 
        className="fixed top-0 left-0 w-8 h-8 rounded-full z-[999] pointer-events-none hidden md:flex items-center justify-center"
        variants={cursorVariants}
        animate={cursorVariant}
        transition={{ type: "spring", stiffness: 500, damping: 28, mass: 0.5 }}
      />

      {/* Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-[3px] bg-[#00f3ff] z-[110] origin-left" style={{ scaleX }} />

      {/* --- TOP NAVIGATION --- */}
      <header className={`fixed top-0 w-full z-[100] transition-all duration-700 ${isScrolled ? 'bg-black/90 backdrop-blur-xl py-4 border-b border-white/5' : 'bg-transparent py-10'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center w-full">
          <Link 
            to="/"
            onMouseEnter={() => setCursorVariant("pointer")} 
            onMouseLeave={() => setCursorVariant("default")}
            className="text-xl font-black uppercase tracking-tighter"
          >
            O'Brian Baxter <span className="text-[#00f3ff] font-medium ml-2 hidden sm:inline">Architect</span>
          </Link>
          
          <nav className="hidden md:flex items-center space-x-12 text-[10px] uppercase tracking-[0.4em] font-bold">
            <Link to="/#about" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="hover:text-[#00f3ff] transition-colors">Legacy</Link>
            <Link to="/#portfolio" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="hover:text-[#00f3ff] transition-colors">Portfolio</Link>
            <a href="https://www.linkedin.com/in/obrian-baxter/" target="_blank" rel="noopener noreferrer" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="hover:text-[#00f3ff] transition-colors">LinkedIn</a>
            <a href="/#contact" onMouseEnter={() => setCursorVariant("pointer")} onMouseLeave={() => setCursorVariant("default")} className="px-6 py-3 bg-white text-black hover:bg-[#00f3ff] transition-all font-black">Inquire</a>
          </nav>

          <button onClick={() => setIsMenuOpen(true)} className="md:hidden p-2 text-[#00f3ff]" aria-label="Menu">
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* --- MOBILE MENU --- */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-0 bg-black z-[200] p-12 flex flex-col justify-between"
          >
            <button onClick={() => setIsMenuOpen(false)} className="self-end p-4 text-[#00f3ff]"><X size={40} /></button>
            <div className="flex flex-col space-y-6">
              <Link to="/#about" onClick={() => setIsMenuOpen(false)} className="text-5xl font-black uppercase tracking-tighter italic text-whitesmoke hover:text-[#00f3ff]">About</Link>
              <Link to="/#portfolio" onClick={() => setIsMenuOpen(false)} className="text-5xl font-black uppercase tracking-tighter italic text-whitesmoke hover:text-[#00f3ff]">Portfolio</Link>
              <a href="#contact" onClick={() => setIsMenuOpen(false)} className="text-5xl font-black uppercase tracking-tighter italic text-whitesmoke hover:text-[#00f3ff]">Contact</a>
            </div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500">{cityLabel}, MN</div>
          </motion.div>
        )}
      </AnimatePresence>

      <main>
        {/* Hero Section */}
        <section className="relative pt-64 pb-20 px-6 md:px-12 max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-2 text-[#00f3ff] uppercase tracking-[0.4em] text-[10px] mb-6 font-bold">
              <MapPin size={12} /> <span>{cityLabel} Hub</span>
            </div>
            <h1 className="text-6xl md:text-9xl font-black uppercase tracking-tighter leading-[0.85] mb-8">
              {serviceData.name.split(' ').map((word, i) => (
                <span key={i} className={i === 1 ? "text-[#00f3ff]" : ""}>{word} <br className="hidden md:block" /></span>
              ))}
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl leading-relaxed max-w-2xl font-light">
              Localized digital architecture for <span className="text-white">{cityLabel}</span>. We engineer high-performance systems tailored to the regional market's specific behavioral patterns.
            </p>
            <div className="mt-12 flex flex-wrap gap-6">
              <a 
                href="#contact" 
                onMouseEnter={() => setCursorVariant("pointer")} 
                onMouseLeave={() => setCursorVariant("default")}
                className="px-8 py-4 bg-white text-black font-black uppercase text-[10px] tracking-widest hover:bg-[#00f3ff] transition-all"
              >
                Start Local Project
              </a>
              <Link to="/" className="px-8 py-4 border border-white/10 text-white font-black uppercase text-[10px] tracking-widest hover:border-[#00f3ff] transition-all">
                Global Legacy
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Strategy Section */}
        <RevealSection className="py-32 px-6 md:px-12 max-w-7xl mx-auto border-t border-white/5">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-7">
              <h2 className="text-4xl md:text-6xl font-bold uppercase tracking-tighter mb-8">
                Why {cityLabel} Trusts <br/><span className="text-[#00f3ff]">Our Systems.</span>
              </h2>
              <p className="text-zinc-500 text-lg leading-relaxed mb-8">
                Local digital architecture is more than design; it’s a strategy built around how {cityLabel} users search, browse, and convert. This includes structured data, fast Core Web Vitals, and modular React components.
              </p>
            </div>
            <div className="lg:col-span-5 space-y-4">
              {serviceData.highlights.map((point, index) => (
                <div 
                  key={index}
                  onMouseEnter={() => setCursorVariant("pointer")} 
                  onMouseLeave={() => setCursorVariant("default")}
                  className="p-8 bg-zinc-900/30 border border-white/5 rounded-2xl hover:border-[#00f3ff]/30 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <Zap className="text-[#00f3ff] shrink-0" size={20} />
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-widest text-zinc-300 mb-2">{serviceData.name}</h4>
                      <p className="text-zinc-500 text-sm leading-relaxed">{point}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </RevealSection>

        {/* CTA Section */}
        <RevealSection id="contact" className="py-32 px-6 md:px-12 max-w-7xl mx-auto">
          <div className="bg-[#111] border border-white/5 p-8 md:p-16 rounded-3xl relative overflow-hidden">
            <div className="relative z-10 max-w-3xl">
              <h3 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6">
                Deploy in {cityLabel}.
              </h3>
              <p className="text-zinc-400 text-lg mb-12 font-light">
                Ready to architect a superior digital presence in the Twin Cities? Mention <span className="text-[#00f3ff] font-bold">Project:{cityLabel}</span> in your inquiry for prioritized local routing.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {[
                  { label: 'Market', val: cityLabel },
                  { label: 'Service', val: serviceData.name },
                  { label: 'Standard', val: 'Twin Cities Optimized' }
                ].map((stat, i) => (
                  <div key={i} className="bg-black/40 border border-white/5 p-6 rounded-xl">
                    <p className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">{stat.label}</p>
                    <p className="text-white font-bold text-sm uppercase">{stat.val}</p>
                  </div>
                ))}
              </div>
              <Link 
                to="/#contact" 
                onMouseEnter={() => setCursorVariant("pointer")} 
                onMouseLeave={() => setCursorVariant("default")}
                className="inline-flex items-center gap-4 bg-[#00f3ff] text-black px-10 py-5 font-black uppercase text-xs tracking-[0.2em] hover:brightness-110 transition-all"
              >
                Initialize Inquiry <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </RevealSection>
      </main>

      <footer className="py-24 border-t border-white/5 text-center px-6">
        <p className="text-[10px] uppercase tracking-[0.8em] opacity-30">
          &copy; {new Date().getFullYear()} O'Brian Baxter &bull; {cityLabel} Division
        </p>
      </footer>
    </div>
  );
};

export default LocationServicesPage;